(() => {
  const init = (root) => {
    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

      // ------------------------------
      // Quantity stepper (accessible)
      // ------------------------------
      const qtyInput = root.querySelector('input[name="quantity"]');
      let updateAtcTotal = () => {};
      const decBtn = root.querySelector('.frame-quantity-input-container .group');
      const incBtn = root.querySelector('.frame-quantity-input-container .plus-wrapper');

      const setQty = (value) => {
        if (!qtyInput) return;
        const min = Number(qtyInput.min || 1);
        const max = qtyInput.max ? Number(qtyInput.max) : Number.POSITIVE_INFINITY;
        const next = clamp(Math.round(value), min, max);
        qtyInput.value = String(next);
        updateAtcTotal();

        if (decBtn) decBtn.disabled = next <= min;
        if (incBtn) incBtn.disabled = next >= max;
      };

      if (qtyInput) {
        // Ensure valid value on load
        const initial = Number(qtyInput.value || 1);
        setQty(Number.isFinite(initial) ? initial : 1);

        qtyInput.addEventListener('change', () => {
          const v = Number(qtyInput.value);
          setQty(Number.isFinite(v) ? v : 1);
        });

        qtyInput.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setQty(Number(qtyInput.value) + 1);
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setQty(Number(qtyInput.value) - 1);
          }
        });
      }

      decBtn?.addEventListener('click', () => {
        if (!qtyInput) return;
        setQty(Number(qtyInput.value) - 1);
      });

      incBtn?.addEventListener('click', () => {
        if (!qtyInput) return;
        setQty(Number(qtyInput.value) + 1);
      });

      // ------------------------------
      // Simple gallery with swipe support and synced thumbnail swiper
      // ------------------------------
      const mainImg = root.querySelector('#galleryMain');
      const thumbsContainer = root.querySelector('.gallery-thumbs.splide');
      const thumbs = Array.from(root.querySelectorAll('.gallery-thumbs .splide__slide .thumb'));
      const prevBtn = root.querySelector('.gallery-nav.prev');
      const nextBtn = root.querySelector('.gallery-nav.next');

      // Build variant to image index mapping
      const variantMap = new Map(); // Maps variant-id to slide index
      const slideVariantMap = new Map(); // Maps slide index to variant-id

      if (thumbs.length > 0) {
        thumbs.forEach((thumb, index) => {
          const variantId = thumb.getAttribute('data-variant-id');
          if (variantId) {
            variantMap.set(variantId, index);
            slideVariantMap.set(index, variantId);
          }
        });
      }

      // Declare setActive outside so it's accessible for variant image updates
      let setActive = null;
      let thumbsSplide = null;
      let isUpdatingFromMain = false;
      let isUpdatingFromThumbs = false;
      let currentIndex = 0;

      if (mainImg && thumbs.length && thumbsContainer) {
        currentIndex = thumbs.findIndex((t) => t.classList.contains('is-active'));
        if (currentIndex < 0) currentIndex = 0;

        // Initialize Splide for thumbnails
        if (typeof Splide !== 'undefined') {
          thumbsSplide = new Splide(thumbsContainer, {
            type: 'slide',
            perPage: 'auto',
            perMove: 1,
            gap: 8,
            padding: { left: 32, right: 32 },
            pagination: false,
            arrows: false,
            drag: 'free',
            snap: true,
            focus: 'center',
            updateOnMove: true,
            startIndex: currentIndex,
            breakpoints: {
              768: {
                gap: 8,
              }
            }
          });
          thumbsSplide.mount();
        }

        setActive = (nextIndex, opts = { focusThumb: false, skipThumbSync: false }) => {
          currentIndex = (nextIndex + thumbs.length) % thumbs.length;
          const src = thumbs[currentIndex].getAttribute('data-src');
          if (src) mainImg.src = src;

          thumbs.forEach((t, i) => {
            const active = i === currentIndex;
            t.classList.toggle('is-active', active);
            if (active) {
              t.setAttribute('aria-current', 'true');
            } else {
              t.removeAttribute('aria-current');
            }
          });

          // Sync thumbnail swiper to show active thumb
          if (!opts.skipThumbSync && thumbsSplide) {
            isUpdatingFromMain = true;
            thumbsSplide.go(currentIndex);
            // Reset flag after a brief delay to allow Splide's moved event to fire if needed
            setTimeout(() => {
              isUpdatingFromMain = false;
            }, 100);
          }

          if (opts.focusThumb) thumbs[currentIndex].focus();
        };

        // Listen to thumbnail swiper changes
        if (thumbsSplide) {
          thumbsSplide.on('moved', (newIndex) => {
            if (!isUpdatingFromMain) {
              isUpdatingFromThumbs = true;
              setActive(newIndex, { skipThumbSync: true });
              isUpdatingFromThumbs = false;
            }
          });
        }

        thumbs.forEach((btn, i) => {
          btn.addEventListener('click', () => {
            setActive(i, { focusThumb: false });
          });
          btn.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setActive(i, { focusThumb: false });
            }
          });
        });

        prevBtn?.addEventListener('click', () => setActive(currentIndex - 1, { focusThumb: false }));
        nextBtn?.addEventListener('click', () => setActive(currentIndex + 1, { focusThumb: false }));

        // Touch swipe on main image
        let startX = 0;
        let startY = 0;
        let tracking = false;

        const onTouchStart = (e) => {
          const t = e.touches?.[0];
          if (!t) return;
          startX = t.clientX;
          startY = t.clientY;
          tracking = true;
        };

        const onTouchMove = (e) => {
          if (!tracking) return;
          const t = e.touches?.[0];
          if (!t) return;

          const dx = t.clientX - startX;
          const dy = t.clientY - startY;

          // If vertical scroll dominates, stop tracking
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
            tracking = false;
          }
        };

        const onTouchEnd = (e) => {
          if (!tracking) return;
          tracking = false;

          const t = e.changedTouches?.[0];
          if (!t) return;
          const dx = t.clientX - startX;

          if (Math.abs(dx) < 40) return;
          if (dx < 0) setActive(currentIndex + 1);
          else setActive(currentIndex - 1);
        };

        const main = mainImg.closest('.gallery');
        main?.addEventListener('touchstart', onTouchStart, { passive: true });
        main?.addEventListener('touchmove', onTouchMove, { passive: true });
        main?.addEventListener('touchend', onTouchEnd, { passive: true });

        // Initialize
        setActive(currentIndex);
      }

      // ------------------------------
      // Details: sync aria-expanded for summary
      // ------------------------------
      root.querySelectorAll('details').forEach((d) => {
        const s = d.querySelector('summary');
        if (!s) return;
        s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
        d.addEventListener('toggle', () => {
          s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
        });
      });

      // ------------------------------
      // Variant selection and availability handling (color and non-color)
      // ------------------------------
      const colorRadios = root.querySelectorAll('.pdp-hero-color-radio');
      const tagRadios = root.querySelectorAll('.pdp-hero-variant-tag-radio');
      const addToCartBtn = root.querySelector('.pdp-hero-add-to-cart');
      const formButtons = root.querySelector('.pdp-hero-form-buttons');
      const engravingBlock = root.querySelector('.pdp-hero-engraving-block');
      const form = root.querySelector('.frame-12');
      const klaviyoTrigger = root.querySelector('.klaviyo-bis-trigger');
      const variantRadios = root.querySelector('variant-radios');
      let shouldOpenCartOnUpdate = false;
      let getCheckedKitBoxes = () => [];

      if (form) {
        form.addEventListener('submit', () => {
          const checked = getCheckedKitBoxes();
          shouldOpenCartOnUpdate = checked.length === 0;
        }, true);
      }

      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (!shouldOpenCartOnUpdate) return;
          const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
          shouldOpenCartOnUpdate = false;
        });
      }
      
      // Get variant data from JSON script
      let variantsData = [];
      if (variantRadios) {
        const variantScript = variantRadios.querySelector('script[type="application/json"]');
        if (variantScript) {
          try {
            variantsData = JSON.parse(variantScript.textContent);
          } catch (e) {
            console.error('Failed to parse variant data', e);
          }
        }
      }

      // Get current variant from variant-radios component
      const getCurrentVariant = () => {
        if (variantRadios && variantRadios.currentVariant) {
          return {
            variantId: variantRadios.currentVariant.id.toString(),
            available: variantRadios.currentVariant.available,
            price: variantRadios.currentVariant.price,
            compareAtPrice: variantRadios.currentVariant.compare_at_price
          };
        }
        
        // Fallback: try to find from custom radios
        const selectedColorRadio = root.querySelector('.pdp-hero-color-radio:checked');
        if (selectedColorRadio && selectedColorRadio.dataset.variantId) {
          const variantId = selectedColorRadio.dataset.variantId;
          const variant = variantsData.find(v => v.id.toString() === variantId);
          if (variant) {
            return {
              variantId: variant.id.toString(),
              available: variant.available,
              price: variant.price,
              compareAtPrice: variant.compare_at_price
            };
          }
        }
        
        return null;
      };
      
      // Update price display
      const updatePrice = (variant) => {
        if (!variant || !variant.price) return;
        
        const priceElements = root.querySelectorAll('.price-item[data-price]');
        const priceContainer = root.querySelector('.price');
        
        // Format price
        const formatMoney = (cents) => {
          if (typeof window.formatMoney === 'function') {
            return window.formatMoney(cents);
          }
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cents / 100);
        };
        
        const formattedPrice = formatMoney(variant.price);
        const formattedComparePrice = variant.compareAtPrice ? formatMoney(variant.compareAtPrice) : null;
        
        // Update price elements
        priceElements.forEach((el) => {
          if (el.classList.contains('price-item--sale') || el.classList.contains('price-item--regular')) {
            el.textContent = formattedPrice;
            el.setAttribute('data-price', variant.price);
          }
        });
        
        // Update compare at price if on sale
        const comparePriceEl = root.querySelector('.price-item--regular[data-price]');
        if (comparePriceEl && variant.compareAtPrice && variant.compareAtPrice > variant.price) {
          const compareAtEl = comparePriceEl.closest('.price__sale')?.querySelector('s');
          if (compareAtEl) {
            compareAtEl.textContent = formattedComparePrice;
            compareAtEl.setAttribute('data-price', variant.compareAtPrice);
          }
          if (priceContainer) {
            priceContainer.classList.add('price--on-sale');
          }
        } else {
          if (priceContainer) {
            priceContainer.classList.remove('price--on-sale');
          }
        }
      };
      
      // Sync custom radios with hidden variant-radios
      const syncVariantRadios = () => {
        if (!variantRadios) return;
        
        const selectedColorRadio = root.querySelector('.pdp-hero-color-radio:checked');
        const selectedTagRadios = root.querySelectorAll('.pdp-hero-variant-tag-radio:checked');
        
        // Get option names from our custom UI
        const selectedOptions = {};
        
        // Get color option
        if (selectedColorRadio) {
          const colorFieldset = selectedColorRadio.closest('[data-color-option]');
          if (colorFieldset) {
            const colorLegend = colorFieldset.querySelector('legend');
            if (colorLegend) {
              const optionName = colorLegend.textContent.replace('Select ', '').trim();
              selectedOptions[optionName] = selectedColorRadio.value;
            }
          }
        }
        
        // Get non-color options
        selectedTagRadios.forEach((tagRadio) => {
          const tagContainer = tagRadio.closest('.pdp-hero-non-color-options');
          if (tagContainer) {
            const tagFieldset = tagContainer.querySelector('fieldset');
            if (tagFieldset) {
              const tagLegend = tagFieldset.querySelector('legend');
              if (tagLegend) {
                const optionName = tagLegend.textContent.replace('Select ', '').trim();
                selectedOptions[optionName] = tagRadio.value;
              }
            }
          }
        });
        
        // Update hidden variant-radios inputs
        const variantRadioInputs = variantRadios.querySelectorAll('input[type="radio"]');
        variantRadioInputs.forEach((input) => {
          const optionName = input.name.replace('options[', '').replace(']', '');
          const inputValue = input.value;
          
          if (selectedOptions[optionName] === inputValue && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      };

      const updateButtonVisibility = (isAvailable, variantId) => {
        // Update hidden variant input for form submission and Klaviyo
        // The input should be inside the form, with class 'product-variant-id'
        const variantInput = form ? form.querySelector('input.product-variant-id[name="id"]') : null;
        if (variantInput && variantId) {
          variantInput.value = variantId;
          // The product-form web component will enable this on form submit
          // But we need to make sure it's set correctly
        }
        
        // Update quantity display visibility
        const qtyContainer = root.querySelector('.frame-quantity-input-container');
        if (qtyContainer) {
          qtyContainer.style.display = isAvailable ? '' : 'none';
        }
        
        // Hide/show entire form buttons container and engraving block when out of stock
        if (formButtons) {
          formButtons.style.display = isAvailable ? '' : 'none';
        }
        if (engravingBlock) {
          engravingBlock.style.display = isAvailable ? '' : 'none';
        }
        
        if (isAvailable) {
          // Show add to cart, hide back in stock
          if (addToCartBtn) {
            addToCartBtn.style.display = '';
            addToCartBtn.disabled = false;
            // Update button text
            const buttonText = addToCartBtn.querySelector('.SHOP-THE-COLLECTION');
            if (buttonText) {
              buttonText.textContent = 'ADD TO CART';
            }
          }
          if (klaviyoTrigger) {
            klaviyoTrigger.style.display = 'none';
          }
        } else {
          // Hide add to cart, show back in stock
          if (addToCartBtn) {
            addToCartBtn.style.display = 'none';
            addToCartBtn.disabled = true;
          }
          if (klaviyoTrigger) {
            klaviyoTrigger.style.display = '';
          }
        }
      };

      // Update gallery image based on variant
      const updateGalleryForVariant = (variantId) => {
        if (!mainImg || !thumbs.length || !setActive) return;
        
        // Check if variant has an associated image
        if (variantId && variantMap.has(variantId)) {
          const slideIndex = variantMap.get(variantId);
          if (slideIndex !== undefined && slideIndex !== null) {
            setActive(slideIndex, { focusThumb: false });
            return;
          }
        }
        
        // Variant doesn't have an associated image - show main/featured image
        // Find the main image (first image without variant ID, or index 0)
        let mainIndex = 0;
        for (let i = 0; i < thumbs.length; i++) {
          if (!thumbs[i].getAttribute('data-variant-id')) {
            mainIndex = i;
            break;
          }
        }
        setActive(mainIndex, { focusThumb: false });
      };
      
      // Update URL with variant parameter while preserving other params
      const updateVariantURL = (variantId) => {
        if (!variantId || !variantRadios || !variantRadios.dataset.url) return;
        
        const url = new URL(window.location.href);
        // Set or replace the variant parameter
        url.searchParams.set('variant', variantId);
        // Update URL without page reload
        window.history.replaceState({}, '', url.toString());
      };
      
      const handleVariantChange = () => {
        // Sync with hidden variant-radios first
        syncVariantRadios();
        
        // Wait a bit for variant-radios to update, then get current variant
        setTimeout(() => {
          const variant = getCurrentVariant();
          if (variant) {
            updateButtonVisibility(variant.available, variant.variantId);
            updatePrice(variant);
            updateVariantURL(variant.variantId);
            updateGalleryForVariant(variant.variantId);
            
            // Dispatch custom event for variant change
            const variantChangeEvent = new CustomEvent('variant-change', {
              detail: { 
                variantId: variant.variantId ? parseInt(variant.variantId, 10) : null, 
                available: variant.available 
              },
              bubbles: true
            });
            root.dispatchEvent(variantChangeEvent);
          }
        }, 100);
      };
      
      // Override variant-radios updateURL to preserve other params
      if (variantRadios && variantRadios.updateURL) {
        const originalUpdateURL = variantRadios.updateURL.bind(variantRadios);
        variantRadios.updateURL = function() {
          if (this.currentVariant && this.dataset.updateUrl !== 'false') {
            updateVariantURL(this.currentVariant.id.toString());
          }
        };
      }
      
      // Listen to variant-radios changes for price and URL updates
      if (variantRadios) {
        // Listen for the change event that variant-radios fires
        variantRadios.addEventListener('change', () => {
          const variant = getCurrentVariant();
          if (variant) {
            updatePrice(variant);
            updateButtonVisibility(variant.available, variant.variantId);
            updateVariantURL(variant.variantId);
            updateGalleryForVariant(variant.variantId);
          }
        });
      }

      // Update tag bubble selected states
      const updateTagBubbleStates = () => {
        root.querySelectorAll('.pdp-hero-variant-tag').forEach((tag) => {
          const radio = root.querySelector(`#${tag.getAttribute('for')}`);
          if (radio && radio.checked) {
            tag.classList.add('pdp-hero-variant-tag--selected');
          } else {
            tag.classList.remove('pdp-hero-variant-tag--selected');
          }
        });
      };

      // Initialize button visibility and price based on selected variant
      updateTagBubbleStates();
      
      // Sync variant-radios on initial load and get initial variant
      syncVariantRadios();
      setTimeout(() => {
        const variant = getCurrentVariant();
        if (variant) {
          updateButtonVisibility(variant.available, variant.variantId);
          updatePrice(variant);
          updateGalleryForVariant(variant.variantId);
        }
      }, 100);

      // Handle color radio changes
      if (colorRadios.length > 0) {
        colorRadios.forEach((radio) => {
          radio.addEventListener('change', (e) => {
            if (!e.target.checked) return;
            handleVariantChange();
          });
        });
      }

      // Handle tag radio changes
      if (tagRadios.length > 0) {
        tagRadios.forEach((radio) => {
          radio.addEventListener('change', (e) => {
            if (!e.target.checked) return;
            updateTagBubbleStates();
            handleVariantChange();
          });
        });
      }

      // ------------------------------
      // Compatible Kits (variant addons)
      // ------------------------------
      const compatibleKits = root.querySelector('[data-compatible-kits]');
      if (compatibleKits) {
        compatibleKits.setAttribute('hidden', '');
        console.log('[Compatible Kits] init', {
          hasToken: Boolean(root.dataset.storefrontApiToken),
          shop: typeof window.Shopify !== 'undefined' ? window.Shopify.shop : null
        });
        const groups = root.querySelectorAll('.pdp-hero-compatible-kits-group');
        const formatMoney = (cents) => {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(cents / 100).replace('.00', '');
        };


        let activeVariantId = null;
        const updateCompatibleKitsContainerVisibility = () => {
          const hasVisibleGroup = Array.from(groups).some((group) => {
            return group.dataset.kitsLoaded === '1' && !group.hasAttribute('hidden');
          });
          if (hasVisibleGroup) {
            compatibleKits.removeAttribute('hidden');
          } else {
            compatibleKits.setAttribute('hidden', '');
          }
        };

        const updateCompatibleKitsVisibility = (currentVariantId) => {
          const variantIdStr = currentVariantId ? String(currentVariantId) : '';
          groups.forEach((group) => {
            const groupVariantId = group.getAttribute('data-variant-id');
            const isLoaded = group.dataset.kitsLoaded === '1';
            if (groupVariantId === variantIdStr && isLoaded) {
              group.removeAttribute('hidden');
            } else {
              group.setAttribute('hidden', '');
            }
          });
          updateCompatibleKitsContainerVisibility();
          // Debug: show current variant + group ids
          let debugEl = root.querySelector('[data-compatible-kits-debug]');
          if (!debugEl && compatibleKits) {
            debugEl = document.createElement('div');
            debugEl.setAttribute('data-compatible-kits-debug', '');
            debugEl.style.cssText = 'margin:0.5rem 0;padding:0.5rem;background:#ffecec;font-size:12px;';
            compatibleKits.prepend(debugEl);
          }
          if (debugEl) {
            const groupIds = Array.from(groups).map((g) => g.getAttribute('data-variant-id')).join(', ');
          }
        };

        const addToCartBtn = root.querySelector('.pdp-hero-add-to-cart');
        const addToCartText = addToCartBtn ? addToCartBtn.querySelector('.SHOP-THE-COLLECTION') : null;
        const defaultAtcText = addToCartText ? addToCartText.textContent.trim() : '';
        const isMainAtcAvailable = () => {
          if (!addToCartBtn) return false;
          if (addToCartBtn.disabled) return false;
          return addToCartBtn.offsetParent !== null;
        };
        const updateKitCheckboxVisibility = () => {
          const show = isMainAtcAvailable();
          root.querySelectorAll('.pdp-hero-compatible-kits-item').forEach((item) => {
            item.classList.toggle('pdp-hero-compatible-kits-item--no-checkbox', !show);
          });
          root.querySelectorAll('.pdp-hero-compatible-kits-checkbox').forEach((cb) => {
            if (!show) {
              cb.checked = false;
              cb.disabled = true;
              cb.style.display = 'none';
              return;
            }
            cb.style.display = '';
            cb.disabled = cb.getAttribute('data-available') !== 'true';
          });
        };
        getCheckedKitBoxes = () => Array.from(root.querySelectorAll('.pdp-hero-compatible-kits-checkbox:checked'));
        const getBaseVariantFallback = () => {
          const variantInput = form ? form.querySelector('input.product-variant-id[name="id"]') : null;
          const priceEl = root.querySelector('.price-item--sale[data-price], .price-item--regular[data-price]');
          const price = priceEl ? parseInt(priceEl.getAttribute('data-price') || '0', 10) : 0;
          return variantInput && variantInput.value ? { variantId: variantInput.value, price, available: true } : null;
        };
        const updateMainAtcTotal = () => {
          if (!isMainAtcAvailable()) return;
          if (!addToCartText) return;
          const variant = getCurrentVariant() || getBaseVariantFallback();
          if (!variant || variant.available === false) return;
          const checked = getCheckedKitBoxes();
          if (checked.length === 0) {
            addToCartText.textContent = defaultAtcText || 'ADD TO CART';
            return;
          }
          const qtyInput = root.querySelector('input[name="quantity"]');
          const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value || '1', 10)) : 1;
          let totalCents = parseInt(variant.price || 0, 10) * qty;
          checked.forEach((cb) => {
            if (cb.getAttribute('data-available') === 'true') {
              totalCents += parseInt(cb.getAttribute('data-price') || '0', 10) * qty;
            }
          });
          addToCartText.textContent = `ADD TO CART • ${formatMoney(totalCents)}`;
        };
        updateAtcTotal = updateMainAtcTotal;

        compatibleKits.addEventListener('change', (e) => {
          if (!isMainAtcAvailable()) return;
          if (e.target.matches('.pdp-hero-compatible-kits-checkbox')) {
            updateMainAtcTotal();
          }
        });
        const qtyInput = root.querySelector('input[name="quantity"]');
        if (qtyInput) {
          qtyInput.addEventListener('change', updateMainAtcTotal);
          qtyInput.addEventListener('input', updateMainAtcTotal);
        }

        root.addEventListener('variant-change', (e) => {
          const variantId = e.detail?.variantId;
          activeVariantId = variantId || null;
          updateCompatibleKitsVisibility(variantId);
          updateKitCheckboxVisibility();
          setTimeout(updateMainAtcTotal, 50);
        });

        const initVariant = getCurrentVariant();
        if (initVariant) {
          activeVariantId = initVariant.variantId;
          updateCompatibleKitsVisibility(initVariant.variantId);
        } else {
          const variantInput = form ? form.querySelector('input.product-variant-id[name="id"]') : null;
          if (variantInput && variantInput.value) {
            activeVariantId = variantInput.value;
            updateCompatibleKitsVisibility(variantInput.value);
          }
        }
        setTimeout(updateMainAtcTotal, 150);
        updateKitCheckboxVisibility();

        // When kits are selected, add all items via main ATC
        const formEl = root.querySelector('form[data-type="add-to-cart-form"]');
        if (formEl) {
          formEl.addEventListener('submit', async (e) => {
            const checked = getCheckedKitBoxes();
            if (checked.length === 0) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            const variant = getCurrentVariant() || getBaseVariantFallback();
            if (!variant || variant.available === false) return;
          const qtyInput = root.querySelector('input[name="quantity"]');
          const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value || '1', 10)) : 1;
            const items = [{ id: variant.variantId, quantity: qty }];
          checked.forEach((cb) => {
              if (cb.getAttribute('data-available') === 'true') {
                const vid = cb.getAttribute('data-variant-id');
              if (vid) items.push({ id: vid, quantity: qty });
              }
            });
            if (!addToCartBtn) return;
            addToCartBtn.disabled = true;
            if (addToCartText) addToCartText.textContent = 'Adding…';
            try {
              const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
              const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
              if (cartDrawer) cartDrawer.setActiveElement?.(document.activeElement);
              const config = typeof fetchConfig === 'function'
                ? fetchConfig('javascript')
                : { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' } };
              config.headers = config.headers || {};
              config.headers['X-Requested-With'] = 'XMLHttpRequest';
              delete config.headers['Content-Type'];
              const formData = new FormData();
              items.forEach((item, index) => {
                formData.append(`items[${index}][id]`, item.id);
                formData.append(`items[${index}][quantity]`, item.quantity);
              });
              if (cartDrawer && cartDrawer.getSectionsToRender) {
                const sectionsToRender = cartDrawer.getSectionsToRender();
                const sectionIds = sectionsToRender.map((section) => section.id);
                formData.append('sections', sectionIds.join(','));
                formData.append('sections_url', window.location.pathname);
              }
              config.body = formData;
              const response = await fetch(cartAddUrl, config);
              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.description || errorData.message || 'Failed to add items to cart');
              }
              const data = await response.json();
              if (data.status) {
                throw new Error(data.description || 'Failed to add items to cart');
              }
              if (!cartDrawer) {
                window.location = window.routes?.cart_url || '/cart';
                return;
              }
              if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
              }
              cartDrawer.renderContents(data);
              if (cartDrawer.open) setTimeout(() => cartDrawer.open(), 100);
              if (addToCartText) addToCartText.textContent = 'Added';
              setTimeout(() => {
                if (addToCartText) addToCartText.textContent = defaultAtcText || 'ADD TO CART';
                addToCartBtn.disabled = false;
                updateMainAtcTotal();
              }, 1500);
            } catch (err) {
              console.error(err);
              if (addToCartText) addToCartText.textContent = defaultAtcText || 'ADD TO CART';
              addToCartBtn.disabled = false;
              updateMainAtcTotal();
            }
          }, true);
        }

      // JS fallback: resolve variant GIDs via Storefront API and render kit items
      const storefrontToken = root.dataset.storefrontApiToken;
      const shop = typeof window.Shopify !== 'undefined' && window.Shopify.shop ? window.Shopify.shop : null;
      if (storefrontToken && shop) {
        console.log('[Compatible Kits] Storefront API enabled', { shop, groupCount: groups.length });
        const loadingGroups = Array.from(root.querySelectorAll('.pdp-hero-compatible-kits-group')).filter((g) => g.querySelector('.pdp-hero-compatible-kits-items-loading'));
        console.log('[Compatible Kits] groups with loading placeholders', loadingGroups.length);
        const apiUrl = `https://${shop}/api/2024-01/graphql.json`;
        const fetchVariantByGid = async (gid) => {
          try {
            const res = await fetch(apiUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': storefrontToken
              },
              body: JSON.stringify({
                query: `query getVariant($id: ID!) { node(id: $id) { ... on ProductVariant { id title availableForSale image { url } price { amount } product { title handle featuredImage { url } } } } }`,
                variables: { id: gid }
              })
            });
            const json = await res.json();
            if (json.errors) {
              console.error('[Compatible Kits] Storefront API errors:', json.errors);
            }
            return json.data?.node || null;
          } catch (err) {
            console.error('[Compatible Kits] Storefront API request failed:', err);
            return null;
          }
        };
        const formatMoneyFallback = (amountStr) => {
          const n = parseFloat(amountStr, 10);
          if (typeof window.formatMoney === 'function' && Number.isFinite(n)) {
            return window.formatMoney(Math.round(n * 100));
          }
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(n);
        };
        loadingGroups.forEach(async (group) => {
          const gidsScript = group.querySelector('script.pdp-hero-compatible-kits-gids');
          const gidsAttr = gidsScript ? gidsScript.textContent : '';
          const itemsContainer = group.querySelector('.pdp-hero-compatible-kits-items');
          const loadingEl = group.querySelector('.pdp-hero-compatible-kits-items-loading');
          if (!itemsContainer || !loadingEl) return;
          let gids = [];
          try {
            const parsed = JSON.parse(gidsAttr || '[]');
            gids = Array.isArray(parsed) ? parsed : [];
          } catch (_) {}
          if (gids.length === 0 && gidsAttr) {
            const matches = gidsAttr.match(/gid:\/\/shopify\/ProductVariant\/\d+/g);
            if (matches) gids = matches;
          }
          console.log('[Compatible Kits] raw gids payload', gidsAttr);
          console.log('[Compatible Kits] group gids', gids);
          if (gids.length === 0) {
            loadingEl.textContent = 'No kits found.';
            return;
          }
          const fragment = document.createDocumentFragment();
          let renderedCount = 0;
          for (const gid of gids) {
            if (!gid || typeof gid !== 'string') continue;
            const variant = await fetchVariantByGid(gid);
            if (!variant) continue;
            const variantId = variant.id ? String(variant.id).split('/').pop() : '';
            const priceCents = Math.round(parseFloat(variant.price?.amount || 0, 10) * 100);
            const available = !!variant.availableForSale;
            const imgUrl = variant.image?.url || variant.product?.featuredImage?.url || '';
            const productHandle = variant.product?.handle || '';
            const productUrl = productHandle ? `${window.location.origin}/products/${productHandle}` : window.location.origin;
            const baseTitle = variant.product?.title || '';
            const variantTitle = variant.title || '';
            const title = baseTitle
              ? (variantTitle && variantTitle !== 'Default Title'
                ? `${baseTitle}: ${variantTitle}`
                : baseTitle)
              : variantTitle;
            const priceFormatted = formatMoneyFallback(variant.price?.amount || '0');
            const safeTitle = title.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const safeAlt = title.replace(/"/g, '&quot;');
            const safeImgUrl = imgUrl.startsWith('//') ? `https:${imgUrl}` : imgUrl;
            const imgHtml = imgUrl
              ? `<img src="${safeImgUrl}" width="48" height="48" alt="${safeAlt}" loading="lazy">`
              : '<span class="pdp-hero-compatible-kits-item-thumb-placeholder"></span>';
            const label = document.createElement('label');
            label.className = 'pdp-hero-compatible-kits-item' + (available ? '' : ' pdp-hero-compatible-kits-item--out-of-stock');
            const checkboxHtml = `<input type="checkbox" class="pdp-hero-compatible-kits-checkbox" data-variant-id="${variantId}" data-price="${priceCents}" data-available="${available}"${available ? '' : ' disabled'}>`;
            label.innerHTML = `
              ${checkboxHtml}
              <span class="pdp-hero-compatible-kits-item-thumb">
                ${imgHtml}
              </span>
              <span class="pdp-hero-compatible-kits-item-info">
                <a href="${productUrl}" class="pdp-hero-compatible-kits-item-title" target="_blank" rel="noopener">${safeTitle}</a>
                <span class="pdp-hero-compatible-kits-item-price">${priceFormatted}</span>
              </span>
            `;
            fragment.appendChild(label);
            renderedCount += 1;
          }
          loadingEl.remove();
          itemsContainer.appendChild(fragment);
          if (renderedCount > 0) {
            group.dataset.kitsLoaded = '1';
          }
          updateCompatibleKitsVisibility(activeVariantId);
          updateMainAtcTotal();
        });
      } else if (compatibleKits) {
        compatibleKits.querySelectorAll('.pdp-hero-compatible-kits-items-loading').forEach((el) => {
          el.textContent = 'Missing Storefront API token.';
        });
        compatibleKits.setAttribute('hidden', '');
      }

      }
  };

  document.querySelectorAll('[data-pdp-hero]').forEach((root) => {
    if (root.dataset.pdpHeroInit === '1') return;
    root.dataset.pdpHeroInit = '1';
    init(root);
  });
})();
